<template>
  <div class="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
    <div class="sm:mx-auto sm:w-full sm:max-w-md">
      <img class="mx-auto h-[100px] w-auto" src="/img/spike-logo.svg" alt="Spike" />
      <h2 class="mt-6 text-center text-3xl font-extrabold text-gray-900">Enter a team or company name</h2>
    </div>

    <div class="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
      <div
        class="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10"
        :class="result === 'TeamCreateResult.Fail' || result === 'TeamCreateResult.AlreadyRegistered' ? 'shakeX' : ''"
      >
        <div class="space-y-6 text-gray-700 text-sm">
          <p>
            Creating a team allows you to have multiple users associated with your account. They will be able to use
            your Spike credits to convert pdfs. You can invite other people to join your team on the next screen.
          </p>
          <p> Enter a team or company name below: </p>

          <div>
            <div class="mt-1">
              <input
                v-model="name"
                name="name"
                type="text"
                autocomplete="off"
                required
                oninput="javascript: if (this.value.length > this.maxLength) this.value = this.value.slice(0, this.maxLength);"
                :maxlength="maxLength"
                :minlength="minLength"
                class="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
              />
            </div>
            <span>Min {{ minLength }} chars, max {{ maxLength }} chars</span>
          </div>

          <div>
            <button
              type="submit"
              class="w-full flex justify-center items-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-600 disabled:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-600"
              :disabled="
                result === 'loading' ||
                result === 'TeamCreateResult.Success' ||
                name === null ||
                name.length < minLength
              "
              @click="onTeamCreate()"
            >
              <div v-if="result === 'loading'">Creating ...</div>
              <div v-else-if="result === 'TeamCreateResult.Success'">Success</div>
              <div v-else>Create</div>
            </button>
          </div>
        </div>
        <div class="mt-6 flex w-full justify-center text-sm">
          <template v-if="result === 'loading'">
            <ScalingDots />
          </template>
          <template v-else-if="result === 'exception'">
            <XCircleIcon class="w-5 h-5 text-red-600" aria-hidden="true" />
            <span class="ml-2 text-red-600">failed to contact the server, please retry</span>
          </template>
          <template v-else-if="result === 'TeamCreateResult.Fail'">
            <XCircleIcon class="w-5 h-5 text-red-600" aria-hidden="true" />
            <span class="ml-2 text-red-600">error, please retry with different name</span>
          </template>
          <template v-else-if="result === 'TeamCreateResult.AlreadyRegistered'">
            <XCircleIcon class="w-5 h-5 text-red-600" aria-hidden="true" />
            <span class="ml-2 text-red-600">team already exists</span>
          </template>
          <template v-else-if="result === 'TeamCreateResult.Success'">
            <CheckCircleIcon class="w-5 h-5 text-green-600" aria-hidden="true" />
            <span class="ml-2 text-green-600">success</span>
          </template>
          <template v-else>
            <!-- dummy - keep height -->
            <span>&nbsp;</span>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  Url,
  TeamCreateRes,
  TeamCreateResult,
  TeamCreateReq,
  ScreenName,
  Flow,
  AppName,
  ScreenRoute,
} from "@spike/app2-sitemap/src/auth.js";
import axios from "axios";
import { CheckCircleIcon, XCircleIcon } from "@heroicons/vue/solid";
import sleep from "@util/sleep";
import { ref, onMounted } from "vue";
import ScalingDots from "../components/spinners/ScalingDots.vue";
import * as Nav from "../navigation";
import { Screen } from "../routes";

const minLength = ref<number>(6);
const maxLength = ref<number>(100);
const name = ref<string | null>(null);
const result = ref<string | null>(null);
// testing:
// const result = ref("loading");
// const result = ref("exception");
// const result = ref("TeamCreateResult.Success");
// const result = ref("TeamCreateResult.Fail");

onMounted(async () => {
  // console.log("onMounted:", ScreenRoute.TeamCreate);
  // multiple flows into this screen, but Nav.startFlow() already called:
  //  - NOTE: oauth = used to redirect here directly from sx after successful {github,google} callback. Now we redirect back to signUp or signIn instead in order to make this logic simpler.
  //  - navigate here during signUp or signIn (incomplete):
  //    - signUp = see Nav.startFlow() in SignUp.vue
  //      - signUp_oauth
  //      - signUp_local
  //    - signIn = see Nav.startFlow() in SignIn.vue
  //      - signIn_oauth_incomplete
  //      - signIn_local_incomplete_email
  //      - signIn_local_incomplete_team
  Nav.prevScreensComplete(ScreenName.TeamCreate);
});

async function onTeamCreate() {
  try {
    const _name = name.value as string;

    result.value = "loading";
    const requestBody: TeamCreateReq = {
      name: _name,
    };

    const response = await axios.post(Url.server.shared.teamCreate, requestBody);
    // console.log("create-team response:", response);
    const res: TeamCreateRes = response.data;

    switch (res.result) {
      case TeamCreateResult.Success:
        onTeamCreateSuccess();
        break;
      case TeamCreateResult.Fail:
        onTeamCreateFail();
        break;
      case TeamCreateResult.AlreadyRegistered:
        onTeamCreateAlreadyRegistered();
        break;
      default:
        onTeamCreateException();
        break;
    }
  } catch (e) {
    // console.error("create-team failed:", e);
    onTeamCreateException();
  }
}

async function onTeamCreateSuccess() {
  // show message
  result.value = "TeamCreateResult.Success";

  // wait 1 second then go to next
  await sleep(1000);
  Nav.complete(Screen.TeamCreate);
  Nav.setActive(Screen.TeamInvite, true);
}

async function onTeamCreateFail() {
  result.value = "TeamCreateResult.Fail";
}

async function onTeamCreateAlreadyRegistered() {
  result.value = "TeamCreateResult.AlreadyRegistered";
}

async function onTeamCreateException() {
  result.value = "exception";
}
</script>
